import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { Icons } from '@components/Icons/Icons';
import { TooltipWrapper } from '@components/Tooltip/Tooltip';
import dynamic from 'next/dynamic';
import { type ReactElement, useEffect, useState } from 'react';
import { Button, ButtonType } from './Button';

const QuickAddModal = dynamic(() =>
  import('../Modal/QuickAddModal').then((mod) => mod.QuickAddModal)
);

type UmamiEventNamePrefix = 'data-umami-event-';
type UmamiEventName = `${UmamiEventNamePrefix}${string}`;

export function QuickAddButton({
  product,
  isMobile = false,
  className,
  ...props
}: {
  product: BaseProduct;
  isMobile?: boolean;
  className?: string;
} & Record<UmamiEventName, string>): ReactElement {
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<undefined | string>();

  useEffect(() => {
    /* this ensure that if the order of products change (ie you unselect a favourited product while viewing it in 'Quick Add'),
     the modal will close and not sure the information for the newly ordered product.
     */
    if (selectedProduct !== product.name) setShowModal(false);
    setSelectedProduct(product.name);
  }, [product]);

  if (!product) return <></>;

  return (
    <BulkErrorBoundary location="QuickAddButton">
      <TooltipWrapper text="Quick Add to Cart">
        <Button
          onClick={() => {
            setShowModal(true);
          }}
          aria-label="quick add button"
          type={ButtonType.submit}
          className={className}
          buttonStyle="tertiary"
          icon={<Icons.CartPlus size="1.5rem" />}
          data-umami-event="hue-quick-add-button-clicked"
          data-umami-event-productid={product.entityId}
          {...props}
        />
      </TooltipWrapper>
      {showModal && (
        <QuickAddModal
          isMobile={isMobile}
          product={product}
          show={showModal}
          setShow={setShowModal}
          compactOverride={false}
        />
      )}
    </BulkErrorBoundary>
  );
}
